import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { AppContent, CapabilityItem, Mode, Zone } from '../lib/types';

interface BoundaryMapProps {
  content: AppContent;
  initialMode: Mode;
  onBack?: () => void;
  onTakeTest?: () => void;
}

const ZONES: Zone[] = ['strong', 'verify', 'human'];

export function BoundaryMap({ content, initialMode, onBack, onTakeTest }: BoundaryMapProps) {
  const { ui } = content;
  const [filter, setFilter] = useState<Mode | 'all'>(initialMode);
  const visible = useMemo(
    () => content.capabilities.filter((item) => filter === 'all' || item.roles.includes(filter)),
    [content.capabilities, filter],
  );
  const [selectedId, setSelectedId] = useState<string>('');
  const selected = visible.find((item) => item.id === selectedId) ?? null;

  function setMode(next: Mode | 'all') {
    setFilter(next);
    setSelectedId('');
  }

  return (
    <main className="map">
      <section className="map-head">
        <p className="eyebrow">{ui.boundary.overline}</p>
        <h1>{ui.boundary.title}</h1>
        <p className="map-lede">{ui.boundary.subtitle}</p>
        <div className="map-filter" role="group" aria-label={ui.assessment.chooseMode}>
          <button
            type="button"
            className={filter === 'all' ? 'is-active' : ''}
            aria-pressed={filter === 'all'}
            onClick={() => setMode('all')}
          >
            {ui.boundary.filterAll}
          </button>
          {(Object.keys(ui.modes) as Mode[]).map((mode) => (
            <button
              key={mode}
              type="button"
              className={filter === mode ? 'is-active' : ''}
              aria-pressed={filter === mode}
              onClick={() => setMode(mode)}
            >
              {ui.modes[mode].label}
            </button>
          ))}
        </div>
      </section>

      {content.capabilityReview ? (
        <details className="map-review">
          <summary>
            <span>{content.capabilityReview.title}</span>
            <time dateTime={content.capabilityReview.asOf}>{content.capabilityReview.asOf}</time>
          </summary>
          <p>{content.capabilityReview.summary}</p>
          <ul>
            {content.capabilityReview.sources.map((source) => (
              <li key={source.id}>
                <a href={source.url} target="_blank" rel="noopener noreferrer">{source.title} ↗</a>
                <time dateTime={source.publishedAt}>{source.publishedAt}</time>
                <p>{source.summary}</p>
              </li>
            ))}
          </ul>
          <p className="map-review-note">{content.capabilityReview.note}</p>
        </details>
      ) : null}

      {ui.boundary.mapAxisX ? (
        <section className="map-field" aria-label={ui.boundary.title}>
          <div className="map-plot">
            <span className="map-plot-y" aria-hidden="true">{ui.boundary.mapAxisY}</span>
            <div className="map-plot-area">
              {visible.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`map-dot map-dot-${item.zone}${selected?.id === item.id ? ' is-open' : ''}`}
                  style={{ left: `${item.uncertainty}%`, bottom: `${item.stakes}%` }}
                  aria-pressed={selected?.id === item.id}
                  onClick={() => setSelectedId(selected?.id === item.id ? '' : item.id)}
                >
                  <span className="sr-only">
                    {item.title} — {ui.boundary.uncertainty} {item.uncertainty},{' '}
                    {ui.boundary.humanStakes} {item.stakes}
                  </span>
                  <i aria-hidden="true" />
                </button>
              ))}
            </div>
            <span className="map-plot-x" aria-hidden="true">{ui.boundary.mapAxisX}</span>
          </div>
          <p className="map-plot-hint">
            {selected ? <b>{selected.title}</b> : null}
            {selected ? selected.summary : ui.boundary.mapHint}
          </p>
        </section>
      ) : null}

      <div className="map-zones">
        {ZONES.map((zone) => {
          const items = visible.filter((item) => item.zone === zone);
          if (!items.length) return null;
          return (
            <section key={zone} className={`map-zone map-zone-${zone}`} aria-labelledby={`zone-${zone}`}>
              <header>
                <h2 id={`zone-${zone}`}>{ui.boundary.zones[zone].title}</h2>
                <p>{ui.boundary.zones[zone].subtitle}</p>
              </header>
              <ul>
                {items.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      aria-expanded={selected?.id === item.id}
                      className={selected?.id === item.id ? 'is-open' : ''}
                      onClick={() => setSelectedId(selected?.id === item.id ? '' : item.id)}
                    >
                      <span>{item.title}</span>
                      <i aria-hidden="true">{selected?.id === item.id ? '−' : '+'}</i>
                    </button>
                    <AnimatePresence initial={false}>
                      {selected?.id === item.id ? <Detail item={item} content={content} /> : null}
                    </AnimatePresence>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>

      <div className="map-actions">
        {onBack ? <button type="button" onClick={onBack}>← {ui.boundary.back}</button> : null}
        {onTakeTest ? (
          <button type="button" className="action-primary" onClick={onTakeTest}>
            {ui.boundary.takeTest}<span aria-hidden="true">→</span>
          </button>
        ) : null}
      </div>
    </main>
  );
}

function Detail({ item, content }: { item: CapabilityItem; content: AppContent }) {
  const { ui } = content;
  const node = useRef<HTMLDivElement | null>(null);

  // The plot and the list are two ways into the same entry, so opening one from
  // the other has to bring the text on screen.
  useEffect(() => {
    node.current?.scrollIntoView({ block: 'nearest' });
  }, [item.id]);

  return (
    <motion.div
      className="map-detail"
      ref={node}
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
    >
      <p className="map-summary">{item.summary}</p>
      <p><b>{ui.boundary.why}</b>{item.why}</p>
      <p><b>{ui.boundary.move}</b>{item.move}</p>
      {item.sourceIds?.length ? (
        <div className="map-sources">
          <span>{ui.boundary.sources}</span>
          {content.capabilityReview?.sources.filter((source) => item.sourceIds?.includes(source.id)).map((source) => (
            <a key={source.id} href={source.url} target="_blank" rel="noopener noreferrer">
              {source.title} ↗
            </a>
          ))}
        </div>
      ) : null}
      <dl>
        <div><dt>{ui.boundary.uncertainty}</dt><dd>{item.uncertainty}</dd></div>
        <div><dt>{ui.boundary.humanStakes}</dt><dd>{item.stakes}</dd></div>
      </dl>
    </motion.div>
  );
}
